import {
  ref,
  useContext,
} from '@nuxtjs/composition-api';
import { defineStore } from 'pinia';

import { documentsApiConfig } from '@/config/api/documents';
import { useClientApi } from '@/plugins/api/lk';
import { MENU_TYPE } from '@/shared/constants';
import type {
  Document,
  Root,
} from '@/types/Interactions';
import type { PageInfo } from '@/types/PageInfo';

import type { StateChanger } from 'vue-infinite-loading';

export const useInteractionsDocuments = defineStore('interaction', () => {
  const { $sentry } = useContext();
  const documents = ref<Document>({
    page: undefined,
    nextPage: undefined,
    result: [],
  });

  async function getDocuments (): Promise<void> {
    try {
      const url = documentsApiConfig.interactions;
      const { data } = await useClientApi().get<Root>(url, {
        params: {
          menu_type: MENU_TYPE.LK_BROKER,
        },
      });
      documents.value.result = data.result;
    } catch (error) {
      console.error('🚀 ~ file: interaction.ts ~ getDocuments ~ error', error);
      $sentry.captureException(error);
    }
  }

  async function getNextPage ({
    page,
    infiniteState,
  }: { page: PageInfo; infiniteState: StateChanger }): Promise<void> {
    try {
      if (!page.next_page) {
        return;
      }

      const { data } = await useClientApi().get<Root>(page.next_page);
      const {
        page_info: pageInfo,
        result,
      } = data;

      if (!pageInfo) {
        throw new Error('page info not received');
      }

      if (result?.length) {
        documents.value.page = pageInfo;
        documents.value.nextPage = pageInfo.next_page;
        documents.value.result.push(...result);

        if (!pageInfo.next_page) {
          infiniteState.complete();
        } else {
          infiniteState.loaded();
        }
      } else {
        infiniteState.loaded();
      }
    } catch (error) {
      console.log('🚀 ~ file: interaction.ts ~ getNextPage ~ error', error);
      infiniteState.complete();
      $sentry.captureException(error);
    }
  }

  return {
    documents,
    getDocuments,
    getNextPage,
  };
},
);
